import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { RichTextEditor, stripHtml } from "@/components/RichText";
import { Plus, Clock, BookOpen, Monitor, Building2, Layers, Video, Search, Star, Pencil, Radio, GraduationCap, Store, Upload, ImagePlus, Award, Link2 } from "lucide-react";
import { CertTemplateDialog } from "@/components/CertTemplateDialog";
import { categoryColors } from "@/lib/helpers";
import { fileToDataUrl } from "@/lib/img";

const CATEGORIES = ["Live CPD Training", "E-learning", "Safeguarding", "SEND", "First Aid", "HR, Compliance and Consultancy", "Training Programmes", "Memberships"];
const MODES = ["online", "classroom", "blended", "virtual"];
const modeIcon = { online: Monitor, classroom: Building2, blended: Layers, virtual: Video };

export default function Courses() {
  const { user } = useAuth();
  const [courses, setCourses] = useState(null);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [certFor, setCertFor] = useState(null);

  const canManage = user.role === "admin";
  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setOpen(true); };

  const load = async () => {
    const { data } = await api.get("/courses");
    setCourses(data);
  };
  useEffect(() => { load(); }, []);

  if (!courses) return <Loading />;

  const filtered = courses.filter(
    (c) => (filter === "All" || (c.categories?.length ? c.categories : [c.category]).includes(filter)) && c.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="Course Catalogue" subtitle="Browse courses, learning paths and prerequisites." testid="courses-header">
        {canManage && (
          <Button onClick={openCreate} data-testid="create-course-button"><Plus className="h-4 w-4 mr-1.5" />New Course</Button>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search courses..." className="pl-9" data-testid="search-courses-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["All", ...CATEGORIES].map((cat) => (
            <button key={cat} onClick={() => setFilter(cat)} data-testid={`filter-category-${cat.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === cat ? "bg-primary text-white border-primary" : "border-border hover:bg-accent/40"}`}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState message="No courses match your filters." icon={BookOpen} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((c) => {
            const MIcon = modeIcon[c.delivery_mode] || Building2;
            return (
              <Card key={c.id} className="overflow-hidden group stagger-in border-border" data-testid={`course-card-${c.id}`}>
                <div className="relative h-36 bg-slate-200 dark:bg-slate-800 overflow-hidden">
                  {c.thumbnail && <img src={c.thumbnail} alt={c.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className={`h-2 w-2 rounded-full ${categoryColors[c.category] || "bg-slate-400"} ring-2 ring-white`} />
                  </div>
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                    {c.is_mandatory && (
                      <Badge className="bg-red-500 hover:bg-red-500 text-white border-0"><Star className="h-3 w-3 mr-1" />Mandatory</Badge>
                    )}
                    {c.is_published && (
                      <Badge className="bg-primary hover:bg-primary text-white border-0"><Store className="h-3 w-3 mr-1" />£{c.price} ex VAT · {(c.course_type || "cpd").toUpperCase()}</Badge>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground mb-2">
                    <span className="font-semibold uppercase tracking-wider">{(c.categories?.length ? c.categories : [c.category]).join(" · ")}</span>
                    {canManage && (
                      <span className="flex items-center gap-3">
                        {c.is_published && (
                          <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/shop?course=${c.id}`); toast.success("Course link copied - paste it into your email"); }} data-testid={`copy-course-link-${c.id}-button`} className="flex items-center gap-1 text-primary hover:underline"><Link2 className="h-3.5 w-3.5" />Copy link</button>
                        )}
                        <button onClick={() => setCertFor(c)} data-testid={`cert-template-course-${c.id}-button`} className="flex items-center gap-1 text-primary hover:underline"><Award className="h-3.5 w-3.5" />Certificate</button>
                        <button onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}-button`} className="flex items-center gap-1 text-primary hover:underline"><Pencil className="h-3.5 w-3.5" />Edit</button>
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading font-semibold text-base leading-snug line-clamp-2 min-h-[2.75rem]">{c.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2" data-testid={`course-synopsis-${c.id}`}>{c.synopsis || stripHtml(c.description)}</p>
                  <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.duration_hours}h</span>
                    <span className="flex items-center gap-1 capitalize"><MIcon className="h-3.5 w-3.5" />{c.delivery_mode}</span>
                    <span className="flex items-center gap-1 ml-auto font-mono">{c.cert_validity_months}mo cert</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {canManage && <CourseDialog open={open} setOpen={setOpen} onSaved={load} course={editing} />}
      {certFor && <CertTemplateDialog course={certFor} onClose={() => setCertFor(null)} />}
    </div>
  );
}

function CourseDialog({ open, setOpen, onSaved, course }) {
  const empty = { title: "", synopsis: "", description: "", objectives: "", category: "Live CPD Training", categories: ["Live CPD Training"], delivery_mode: "classroom", duration_hours: 2, is_mandatory: false, cert_validity_months: 12, passing_score: 70, cost: 0, prerequisites: [], price: 0, course_type: "cpd", is_published: false, join_link: "", content: "", cpd_points: 0, billing: "one_time", free_for_members: false, member_plans: [], register_interest: false };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/membership-plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (course) setForm({ ...empty, ...course, categories: course.categories?.length ? course.categories : [course.category] });
    else setForm(empty);
  }, [course, open]);

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    if (!form.categories.length) return toast.error("Pick at least one category");
    setSaving(true);
    try {
      const thumbs = {
        Compliance: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80",
        Technical: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&q=80",
        Leadership: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&q=80",
        Safety: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&q=80",
      };
      const payload = { ...form, duration_hours: Number(form.duration_hours), cert_validity_months: Number(form.cert_validity_months), passing_score: Number(form.passing_score), cost: Number(form.cost), price: Number(form.price), cpd_points: Number(form.cpd_points), renewal_months: Number(form.renewal_months) > 0 ? Number(form.renewal_months) : null, category: form.categories[0], thumbnail: form.thumbnail || thumbs[form.categories[0]] };
      if (course) {
        await api.put(`/courses/${course.id}`, payload);
        toast.success("Course updated");
      } else {
        await api.post("/courses", payload);
        toast.success("Course created");
      }
      setForm(empty);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{course ? "Edit Course" : "Create Course"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" data-testid="course-title-input" /></div>
          <div><Label>Short synopsis <span className="text-muted-foreground font-normal">(1-3 sentences shown on the course card)</span></Label><Textarea rows={3} maxLength={600} value={form.synopsis || ""} onChange={(e) => set("synopsis", e.target.value)} placeholder="A short summary of the course..." className="mt-1.5" data-testid="course-synopsis-input" /><p className="text-xs text-muted-foreground mt-1">{(form.synopsis || "").length}/600</p></div>
          <div><Label>Full course content <span className="text-muted-foreground font-normal">(shown when learners click "View course info" - paste as much as you like, emojis welcome)</span></Label><RichTextEditor value={form.description} onChange={(v) => set("description", v)} testid="course-description-input" tall /></div>

          <div>
            <Label>Course photo</Label>
            <div className="flex items-center gap-4 mt-1.5">
              <div className="h-20 w-28 rounded-lg border border-border bg-accent/30 overflow-hidden flex items-center justify-center shrink-0">
                {form.thumbnail ? <img src={form.thumbnail} alt="course" className="h-full w-full object-cover" /> : <ImagePlus className="h-6 w-6 text-muted-foreground" />}
              </div>
              <div className="flex-1 space-y-2">
                <label className="cursor-pointer inline-block">
                  <input type="file" accept="image/*" className="hidden" data-testid="course-photo-upload"
                    onChange={async (e) => { const f = e.target.files?.[0]; if (f) { try { set("thumbnail", await fileToDataUrl(f)); } catch { toast.error("Could not read image"); } } }} />
                  <span className="inline-flex items-center px-3 py-2 rounded-lg border border-border text-sm hover:bg-accent/40"><Upload className="h-4 w-4 mr-1.5" />Upload photo</span>
                </label>
                <Input value={typeof form.thumbnail === "string" && form.thumbnail.startsWith("http") ? form.thumbnail : ""} onChange={(e) => set("thumbnail", e.target.value)} placeholder="…or paste an image URL" data-testid="course-photo-url" />
              </div>
            </div>
          </div>
          <div><Label>Learning Objectives</Label><Textarea value={form.objectives} onChange={(e) => set("objectives", e.target.value)} className="mt-1.5" placeholder="Separate with semicolons" /></div>
          <div>
            <Label>Categories <span className="text-muted-foreground font-normal">(tick all that apply)</span></Label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="course-categories-picker">
              {CATEGORIES.map((c) => {
                const on = form.categories.includes(c);
                return (
                  <button type="button" key={c} data-testid={`course-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => set("categories", on ? form.categories.filter((x) => x !== c) : [...form.categories, c])}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-primary text-white border-primary" : "border-border hover:bg-accent/40"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Delivery Mode</Label>
              <Select value={form.delivery_mode} onValueChange={(v) => set("delivery_mode", v)}>
                <SelectTrigger className="mt-1.5" data-testid="course-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Hours</Label><Input type="number" value={form.duration_hours} onChange={(e) => set("duration_hours", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Internal cert (mo)</Label><Input type="number" value={form.cert_validity_months} onChange={(e) => set("cert_validity_months", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Pass %</Label><Input type="number" value={form.passing_score} onChange={(e) => set("passing_score", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Certificate renewal (months) <span className="text-muted-foreground font-normal">(e.g. 36 for Paediatric First Aid - leave blank if it never expires; members and learners are emailed 30 days before)</span></Label><Input type="number" min="0" value={form.renewal_months ?? ""} onChange={(e) => set("renewal_months", e.target.value)} placeholder="Never expires" className="mt-1.5" data-testid="course-renewal-months-input" /></div>
          <div><Label>Cost per seat ($)</Label><Input type="number" value={form.cost} onChange={(e) => set("cost", e.target.value)} className="mt-1.5" /></div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div><p className="text-sm font-medium">Mandatory / Compliance</p><p className="text-xs text-muted-foreground">Required for assigned learners</p></div>
            <Switch checked={form.is_mandatory} onCheckedChange={(v) => set("is_mandatory", v)} data-testid="course-mandatory-switch" />
          </div>

          <div className="rounded-lg border border-border p-4 space-y-4">
            <div className="flex items-center gap-2"><Store className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Public Course Shop</p></div>
            <div>
              <Label>Course type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[{ v: "cpd", l: "Self-paced CPD", i: GraduationCap }, { v: "live", l: "Live session", i: Radio }].map((t) => (
                  <button type="button" key={t.v} onClick={() => set("course_type", t.v)} data-testid={`course-type-${t.v}`}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-colors ${form.course_type === t.v ? "border-primary bg-accent/50 text-primary font-medium" : "border-border"}`}>
                    <t.i className="h-4 w-4" />{t.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Sale price (£, ex VAT){form.billing === "monthly" && <span className="text-muted-foreground font-normal"> /month</span>}</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5" data-testid="course-price-input" /></div>
              <div><Label>CPD points</Label><Input type="number" value={form.cpd_points} onChange={(e) => set("cpd_points", e.target.value)} className="mt-1.5" /></div>
            </div>
            {form.course_type === "live" ? (
              <div><Label>Live join link (Zoom/Meet URL)</Label><Input value={form.join_link || ""} onChange={(e) => set("join_link", e.target.value)} placeholder="https://zoom.us/j/..." className="mt-1.5" data-testid="course-joinlink-input" /></div>
            ) : (
              <div><Label>CPD content (one item per line — paste a URL to show a clickable link)</Label><Textarea value={form.content || ""} onChange={(e) => set("content", e.target.value)} placeholder={"https://your-cpd-platform.com/course/123\nor Module 1: ..."} className="mt-1.5" data-testid="course-content-input" /></div>
            )}
            <div>
              <Label>Handouts link <span className="text-muted-foreground font-normal">(emailed to everyone booked, 3 hours after each session ends)</span></Label>
              <Input value={form.handouts_link || ""} onChange={(e) => set("handouts_link", e.target.value)} placeholder="https://drive.google.com/..." className="mt-1.5" data-testid="course-handouts-link-input" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><p className="text-sm font-medium">Monthly subscription</p><p className="text-xs text-muted-foreground">Bill this as a recurring membership</p></div>
              <Switch checked={form.billing === "monthly"} onCheckedChange={(v) => set("billing", v ? "monthly" : "one_time")} data-testid="course-billing-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><p className="text-sm font-medium">Free for members</p><p className="text-xs text-muted-foreground">Active members unlock this free</p></div>
              <Switch checked={form.free_for_members} onCheckedChange={(v) => set("free_for_members", v)} data-testid="course-free-members-switch" />
            </div>
            {form.free_for_members && (
              <div className="rounded-lg border border-border p-3">
                <p className="text-sm font-medium">Unlocked by memberships</p>
                <p className="text-xs text-muted-foreground mb-2">Tick the plans that unlock this course. Leave all unticked to unlock it for every membership.</p>
                <div className="flex flex-wrap gap-2">
                  {plans.length === 0 && <span className="text-xs text-muted-foreground">No membership plans yet — create a course in the "Memberships" category first.</span>}
                  {plans.map((p) => {
                    const on = (form.member_plans || []).includes(p);
                    return (
                      <button type="button" key={p} data-testid={`course-plan-${p}`}
                        onClick={() => set("member_plans", on ? form.member_plans.filter((x) => x !== p) : [...(form.member_plans || []), p])}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-primary text-white border-primary" : "border-border hover:bg-accent/40"}`}>
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><p className="text-sm font-medium">Register interest only</p><p className="text-xs text-muted-foreground">Show a "Register interest" button instead of the price</p></div>
              <Switch checked={!!form.register_interest} onCheckedChange={(v) => set("register_interest", v)} data-testid="course-register-interest-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><p className="text-sm font-medium">Publish to shop</p><p className="text-xs text-muted-foreground">Make purchasable by candidates</p></div>
              <Switch checked={form.is_published} onCheckedChange={(v) => set("is_published", v)} data-testid="course-publish-switch" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-course-button">{saving ? "Saving..." : course ? "Save Changes" : "Create Course"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
